import axios from "axios";
import {
  ComponentRole,
  ComponentRoleAssignmentUserOption,
  Group,
  GroupsAndUsersResponse,
  QuotaPeriod,
  UserOption,
} from "shared/models";
import { DASHBOARD_BASE_URL, GROUPS_BASE_URL } from "shared/constants/backend";
import { generateAuthorizationHeaders } from "shared/utils";

export async function createGroup(group: Group, token = ""): Promise<Group> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.post(`${GROUPS_BASE_URL}/`, group, {
    headers,
  });

  return response.data;
}

export async function updateGroup(
  groupName: string,
  group: Group,
  token = ""
): Promise<void> {
  const headers = generateAuthorizationHeaders(token);

  const response = await axios.patch(
    `${GROUPS_BASE_URL}/${groupName}`,
    { ...group },
    {
      headers,
    }
  );

  return response.data;
}

export async function deleteGroup(groupId: string, token = ""): Promise<void> {
  const headers = generateAuthorizationHeaders(token);

  const response = await axios.delete(`${GROUPS_BASE_URL}/${groupId}/`, {
    headers,
  });

  return response.data;
}

export async function selfJoinGroup(
  groupId: string,
  token = ""
): Promise<void> {
  const headers = generateAuthorizationHeaders(token);

  await axios.post(`${GROUPS_BASE_URL}/${groupId}/members/me/`, undefined, {
    headers,
  });
}

export async function editGroup(group: Group, token = ""): Promise<Group> {
  const headers = generateAuthorizationHeaders(token);

  const response = await axios.patch(`${GROUPS_BASE_URL}/edit/`, group, {
    headers,
  });

  return response.data;
}

export async function getAllGroupsAndUsers(
  token = ""
): Promise<GroupsAndUsersResponse> {
  const headers = generateAuthorizationHeaders(token);

  const response = await axios.get<GroupsAndUsersResponse>(
    `${DASHBOARD_BASE_URL}/`,
    {
      headers,
    }
  );
  return response.data;
}

export async function getGroupComponentRoleAssignments(
  groupId: string,
  componentId: number,
  roleId: number,
  token = ""
): Promise<string[]> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.get<string[]>(
    `${GROUPS_BASE_URL}/${groupId}/components/${componentId}/roles/${roleId}/assignments/`,
    { headers }
  );
  return response.data;
}

export async function getGroupComponentRoles(
  groupId: string,
  componentId: number,
  token = ""
): Promise<ComponentRole[]> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.get<ComponentRole[]>(
    `${GROUPS_BASE_URL}/${groupId}/components/${componentId}/roles/`,
    { headers }
  );
  return response.data;
}

export async function getGroupComponentRoleAssignmentUserOptions(
  groupId: string,
  componentId: number,
  token = ""
): Promise<ComponentRoleAssignmentUserOption[]> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.get<ComponentRoleAssignmentUserOption[]>(
    `${GROUPS_BASE_URL}/${groupId}/components/${componentId}/role-assignment-user-options/`,
    { headers }
  );
  return response.data;
}

export async function updateGroupComponentRoleAssignments(
  groupId: string,
  componentId: number,
  roleId: number,
  userIds: string[],
  token = ""
): Promise<string[]> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.put<string[]>(
    `${GROUPS_BASE_URL}/${groupId}/components/${componentId}/roles/${roleId}/assignments/`,
    { userIds },
    { headers }
  );
  return response.data;
}

export async function getGroupExternalUsers(
  groupId: string,
  token = ""
): Promise<UserOption[]> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.get<UserOption[]>(
    `${GROUPS_BASE_URL}/${groupId}/external-users/`,
    { headers }
  );
  return response.data;
}

export type GroupExternalUserCounts = Record<string, number>;

export async function getGroupExternalUserCounts(
  groupIds: string[],
  token = ""
): Promise<GroupExternalUserCounts> {
  const headers = generateAuthorizationHeaders(token);
  const response = await axios.post<GroupExternalUserCounts>(
    `${GROUPS_BASE_URL}/external-user-counts/`,
    { groupIds },
    { headers }
  );
  return response.data;
}

export interface GroupQuotaSummary {
  total_quota: number;
  remaining_quota: number;
  group_name?: string | null;
  quota_end_date?: number | null;
  period: QuotaPeriod | null;
}

export async function getGroupQuotaSummary(
  billingId: string,
  token = ""
): Promise<GroupQuotaSummary> {
  const headers = generateAuthorizationHeaders(token);

  const response = await axios.get<GroupQuotaSummary>(
    `${GROUPS_BASE_URL}/quota/${billingId}`,
    {
      headers,
    }
  );
  return response.data;
}

export interface GroupQuotaConfig {
  quotaAmount: number;
  period: QuotaPeriod;
  startDate: string;
  endDate: string;
  effortType: string;
}

export async function getGroupQuotaConfigs(
  billingId: string,
  token = ""
): Promise<GroupQuotaConfig[]> {
  const headers = generateAuthorizationHeaders(token);

  const response = await axios.get<GroupQuotaConfig[]>(
    `${GROUPS_BASE_URL}/quota/${billingId}/configs`,
    { headers }
  );
  return response.data;
}
